import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { Section, SectionTitle } from '@/components/Section';
import { Button } from '@/components/ui/button';
import { CareerEntry } from '@/components/CareerEntry';

const Paragraph = ({ children, delay = 0 }) => (
  <motion.p
    className="text-lg text-slate-300 leading-relaxed"
    initial={{ opacity: 0, y: 15 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
  >
    {children}
  </motion.p>
);

export default function CareerHistoryPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button asChild variant="ghost" className="text-primary hover:bg-primary/10 mb-6 -ml-3" data-testid="back-to-career-btn">
              <Link to="/career">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Career
              </Link>
            </Button>
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="career-history-headline"
          >
            Career History
          </motion.h1>

          <motion.p
            className="text-xl text-primary font-medium italic mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            A Complete Record of Experience, Growth, and Perspective
          </motion.p>

          <motion.div
            className="space-y-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <p className="text-lg text-slate-300 leading-relaxed">
              My career has never followed a straight or predictable path. It has been shaped by opportunity, necessity, curiosity, and a willingness to step into unfamiliar territory.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed">
              What you'll find here is not just a list of jobs—it's a record of how perspective is built over time. Every role, whether technical, operational, or customer-facing, contributed to how I understand systems, people, and business today.
            </p>
            <p className="text-slate-400">
              This page exists for those who want the full picture.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="download-history-pdf-btn">
              <a href="/assets/R_Dale_Brunet_Career_History.pdf" download>
                <Download className="mr-2 h-4 w-4" />
                Download Full Career History (PDF)
              </a>
            </Button>
          </motion.div>
        </div>
      </Section>

      {/* Early Foundations */}
      <Section dark>
        <div className="max-w-4xl mx-auto">
          <SectionTitle subtitle="1979–1990">Early Foundations</SectionTitle>
          <p className="text-slate-400 italic mb-8">Where work ethic, adaptability, and real-world perspective were formed</p>

          <div className="space-y-8">
            <CareerEntry company="Halliburton Services" period="1979–1981" role="Cementing Technician">
              <Paragraph>
                Worked in oil field operations supporting cementing services under demanding and high-pressure conditions. This role required precision, coordination, and accountability—introducing the importance of systems that must perform reliably in real-world environments.
              </Paragraph>
            </CareerEntry>

            <CareerEntry company="Datec, Inc." period="1982–1983" role="Computer Operator">
              <Paragraph>
                Transitioned into early computing environments, gaining exposure to enterprise systems and operational data processing.
              </Paragraph>
            </CareerEntry>

            <CareerEntry company="Louisiana World Exposition" period="1983–1984" role="Computer Operator">
              <Paragraph>
                Supported large-scale event systems, reinforcing the role of computing in logistics and coordination.
              </Paragraph>
            </CareerEntry>

            <CareerEntry company="Systems Development Corporation / Unisys" period="1984–1987" role="Computer Operator → Technical Writer → Resource Management Analyst">
              <Paragraph>
                Based at NAS Pt. Mugu in California, this was my first experience with DOD contracting. Expanded responsibilities into documentation, system understanding, and operational planning. This role marked the transition from working within systems to understanding how they are structured, managed, and communicated.
              </Paragraph>
            </CareerEntry>

            <motion.div
              className="bg-card/40 border border-border/20 rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="mb-4 space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-lg font-bold text-white">C&R Clothiers</h3>
                  <span className="text-sm text-primary font-medium">1987–1989</span>
                  <span className="text-sm text-slate-500">— Men's Clothing Consultant</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-lg font-bold text-white">Rubenstein Brothers</h3>
                  <span className="text-sm text-primary font-medium">1989</span>
                  <span className="text-sm text-slate-500">— Men's Clothing Consultant</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-lg font-bold text-white">Shop For Value</h3>
                  <span className="text-sm text-primary font-medium">1989–1990</span>
                  <span className="text-sm text-slate-500">— Advertising Sales</span>
                </div>
              </div>
              <Paragraph>
                These customer-facing roles developed communication skills, sales awareness, and an understanding of how value is perceived—insights that continue to influence product design and business strategy today.
              </Paragraph>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Enterprise Systems */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <SectionTitle subtitle="1990–2005">Enterprise Systems & Technology Evolution</SectionTitle>
          <p className="text-slate-400 italic mb-8">Building depth in software, systems, and business technology</p>

          <div className="space-y-5">
            <Paragraph>
              During this period, my work became increasingly focused on software systems, database technologies, and enterprise environments. I contributed to the development, support, and evolution of systems that supported real business operations.
            </Paragraph>
            <Paragraph>
              This phase established the technical foundation that would later support mission-critical and large-scale system work.
            </Paragraph>
          </div>
        </div>
      </Section>

      {/* Defense & Mission-Critical */}
      <Section dark>
        <div className="max-w-4xl mx-auto">
          <SectionTitle subtitle="2006–2011">Defense & Mission-Critical Systems</SectionTitle>
          <p className="text-slate-400 italic mb-8">Where reliability, scale, and precision became non-negotiable</p>

          <div className="space-y-5 mb-8">
            <Paragraph>
              Worked on systems supporting the Air Force Satellite Control Network (AFSCN), contributing to applications that required high reliability, security, and performance.
            </Paragraph>
            <Paragraph>
              These systems were not theoretical. They were operational, high-stakes environments where performance and accuracy were essential.
            </Paragraph>
          </div>

          <motion.div
            className="bg-card/40 border border-border/20 rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-white font-medium mb-3">This phase reinforced:</p>
            <ul className="space-y-2">
              {[
                'System integrity',
                'Operational continuity',
                'Clear architecture under pressure',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* Entrepreneurship */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <SectionTitle subtitle="2010–2024">Entrepreneurship & Real-World Execution</SectionTitle>
          <p className="text-slate-400 italic mb-8">Where theory meets consequence</p>

          <div className="space-y-5 mb-8">
            <Paragraph>
              Founded and operated multiple ventures, including:
            </Paragraph>
          </div>

          <motion.div
            className="bg-card/40 border border-border/20 rounded-xl p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-bold text-white mb-3">Momma Pearl's Cajun Kitchen</h3>
            <Paragraph>
              Built and operated a full-service restaurant, managing staff, operations, supply chain, customer experience, and financial performance.
            </Paragraph>
          </motion.div>

          <motion.div
            className="bg-card/40 border border-border/20 rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-white font-medium mb-3">This phase grounded my understanding of:</p>
            <ul className="space-y-2">
              {[
                'Margins and cost control',
                'Customer behavior in real time',
                'The difference between ideas and execution',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* AI, Publishing & Platforms */}
      <Section dark>
        <div className="max-w-4xl mx-auto">
          <SectionTitle subtitle="Current">AI, Publishing & Platform Development</SectionTitle>
          <p className="text-slate-400 italic mb-8">Where experience converges into creation</p>

          <Paragraph>
            Current focus includes the development of multiple platforms:
          </Paragraph>

          <div className="grid sm:grid-cols-2 gap-4 mt-6 mb-8">
            {[
              { name: 'LifeCapsule AI', desc: 'Digital legacy and memory preservation' },
              { name: 'DigitalInkAI', desc: 'AI-assisted writing and publishing tools' },
              { name: 'NewAudia', desc: 'Interactive audiobook platform' },
              { name: 'AI Means Business', desc: 'Practical AI consulting for small and mid-sized businesses' },
            ].map((platform, i) => (
              <motion.div
                key={platform.name}
                className="bg-card/40 border border-border/20 rounded-xl p-5"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <h3 className="text-white font-semibold mb-1">{platform.name}</h3>
                <p className="text-sm text-slate-400">{platform.desc}</p>
              </motion.div>
            ))}
          </div>

          <Paragraph>
            These efforts combine decades of experience across systems, business, and human behavior into solutions designed for real-world use.
          </Paragraph>
        </div>
      </Section>

      {/* Closing Perspective */}
      <Section>
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-testid="closing-perspective">Closing Perspective</h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-4">
              I do not view my career as a sequence of unrelated roles.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              I view it as a continuous process of learning how systems work—whether those systems involve technology, people, or business itself. That perspective is what I bring to every project I work on today.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="download-history-pdf-bottom">
                <a href="/assets/R_Dale_Brunet_Career_History.pdf" download>
                  <Download className="mr-2 h-4 w-4" />
                  Download Full Career History (PDF)
                </a>
              </Button>
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="schedule-from-history-btn">
                <Link to="/schedule">Schedule a Conversation</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </Section>
    </main>
  );
}
